import { useState } from 'react';
import { Copy, Check, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useCreateApiKey } from '../hooks/useApiKeys';
import { AVAILABLE_SCOPES } from '../types';

interface CreateKeyDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateKeyDialog({ open, onClose }: CreateKeyDialogProps) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['kpi:read', 'kpi:write']);
  const [rateLimitRpm, setRateLimitRpm] = useState(60);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createKey = useCreateApiKey();

  const handleCreate = async () => {
    const result = await createKey.mutateAsync({
      name,
      scopes,
      rateLimitRpm,
    });
    setRawKey(result.rawKey);
  };

  const handleCopy = () => {
    if (rawKey) {
      navigator.clipboard.writeText(rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setName('');
    setScopes(['kpi:read', 'kpi:write']);
    setRateLimitRpm(60);
    setRawKey(null);
    setCopied(false);
    onClose();
  };

  const toggleScope = (scope: string) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  if (rawKey) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API Key Created</DialogTitle>
            <DialogDescription>
              Copy your API key now. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
              <p className="text-sm text-amber-700 dark:text-amber-400">
                This is the only time the full key will be displayed. Store it securely.
              </p>
            </div>
            <div className="relative">
              <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 text-sm font-mono break-all pr-12">
                {rawKey}
              </pre>
              <button
                onClick={handleCopy}
                className="absolute top-3 right-3 p-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-zinc-200"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleClose}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            Generate a new API key for external integrations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="key-name">Name</Label>
            <Input
              id="key-name"
              placeholder="e.g. Monthly KPI cron job"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label>Scopes</Label>
            <div className="mt-2 space-y-2">
              {AVAILABLE_SCOPES.map((scope) => (
                <label key={scope.value} className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={scopes.includes(scope.value)}
                    onCheckedChange={() => toggleScope(scope.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <span className="text-sm font-medium">{scope.label}</span>
                    <p className="text-xs text-muted-foreground">{scope.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="rate-limit">Rate limit (requests/min)</Label>
            <Input
              id="rate-limit"
              type="number"
              min={1}
              max={1000}
              value={rateLimitRpm}
              onChange={(e) => setRateLimitRpm(Number(e.target.value))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || scopes.length === 0 || createKey.isPending}
          >
            {createKey.isPending ? 'Creating...' : 'Create Key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
